/**
 * Common Constants
 * Centralized constants used across the application
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'https://dev.services.leadconnectorhq.com',
  ENDPOINTS: {
    AUTH: '/hltestmanager/auth',
    USERS: '/hltestmanager/users',
    TEAMS: '/hltestmanager/teams',
    PROJECTS: '/hltestmanager/projects',
    TEST_CASES: '/hltestmanager/test_cases',
    TEST_RUNS: '/hltestmanager/test_runs',
    BUGS: '/hltestmanager/bugs',
    FOLDERS: '/hltestmanager/folders',
    FEATURES: '/hltestmanager/features',
    REPORTS: '/hltestmanager/reports'
  },
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3
}

// Common Status Values
export const STATUS_VALUES = {
  // General statuses
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DRAFT: 'draft',
  PENDING: 'pending',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  
  // Test case statuses
  TEST_CASE: {
    DRAFT: 'draft',
    ACTIVE: 'active',
    DEPRECATED: 'deprecated'
  },
  
  // Test run statuses
  TEST_RUN: {
    YET_TO_START: 'yet_to_start',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled'
  },
  
  // Bug statuses
  BUG: {
    OPEN: 'open',
    IN_PROGRESS: 'in_progress',
    RESOLVED: 'resolved',
    CLOSED: 'closed',
    REOPENED: 'reopened',
    BLOCKED: 'blocked',
    NOT_A_BUG: 'not_a_bug',
    SETUP_ISSUE: 'setup_issue'
  },
  
  // Project statuses
  PROJECT: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    COMPLETED: 'completed',
    ON_HOLD: 'on_hold'
  }
}

// Priority Levels
export const PRIORITY_LEVELS = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
}

// Query Parameter Values
export const QUERY_PARAMS = {
  DELETED: 'deleted',
  VALUE_TRUE: 'true',
  VALUE_FALSE: 'false'
}

// User Roles
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  QA_MANAGER: 'qa_manager',
  DEVELOPER: 'developer',
  TESTER: 'tester'
}

// Visibility Options
export const VISIBILITY_OPTIONS = {
  PUBLIC: 'public',
  PRIVATE: 'private'
}

// Length Limits for Teams and Projects
export const LENGTH_LIMITS = {
  TEAM_TITLE_MAX: 60,
  TEAM_DESCRIPTION_MAX: 100,
  PROJECT_TITLE_MAX: 60,
  PROJECT_DESCRIPTION_MAX: 100
}

// User Role Labels (Display Names)
export const USER_ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  qa_manager: 'QA Manager',
  developer: 'Developer',
  tester: 'Tester'
}

// Common Messages
export const MESSAGES = {
  LOADING: {
    DEFAULT: 'Loading...',
    TEST_CASES: 'Loading test cases...',
    TEST_RUNS: 'Loading test runs...',
    BUGS: 'Loading bugs...',
    PROJECTS: 'Loading projects...',
    USERS: 'Loading users...',
    REPORTS: 'Loading reports...'
  },
  
  SUCCESS: {
    CREATED: 'Created successfully',
    UPDATED: 'Updated successfully',
    DELETED: 'Deleted successfully',
    SAVED: 'Saved successfully',
    CLONED: 'Cloned successfully',
    ASSIGNED: 'Assigned successfully'
  },
  
  ERROR: {
    GENERIC: 'An error occurred',
    NETWORK: 'Network error. Please check your connection.',
    VALIDATION: 'Please check your input and try again.',
    NOT_FOUND: 'Resource not found',
    UNAUTHORIZED: 'You are not authorized to perform this action',
    FORBIDDEN: 'Access denied',
    SERVER: 'Server error. Please try again later.'
  },
  
  CONFIRMATION: {
    DELETE: 'Are you sure you want to delete this item?',
    DELETE_MULTIPLE: 'Are you sure you want to delete these items?',
    CANCEL: 'Are you sure you want to cancel?',
    RESET: 'Are you sure you want to reset all changes?'
  }
}

// Common Validation Rules
export const VALIDATION_RULES = {
  REQUIRED: 'This field is required',
  MIN_LENGTH: (min) => `Must be at least ${min} characters long`,
  MAX_LENGTH: (max) => `Must be no more than ${max} characters long`,
  EMAIL: 'Please enter a valid email address',
  URL: 'Please enter a valid URL',
  NUMBER: 'Must be a valid number',
  POSITIVE_NUMBER: 'Must be a positive number',
  UNIQUE: 'This value already exists',
  INVALID_FORMAT: 'Invalid format'
}

// Common CSS Classes
export const CSS_CLASSES = {
  LOADING_SPINNER: 'animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto',
  LOADING_TEXT: 'mt-2 text-gray-500',
  EMPTY_STATE: 'text-center py-12',
  CARD: 'bg-white dark:bg-gray-800 rounded-lg shadow',
  CARD_HEADER: 'px-6 py-4 border-b border-gray-200 dark:border-gray-700',
  CARD_BODY: 'p-6',
  CARD_FOOTER: 'px-6 py-4 border-t border-gray-200 dark:border-gray-700',
  BUTTON_GROUP: 'flex items-center space-x-3',
  FORM_GROUP: 'space-y-4',
  FORM_ROW: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
  TABLE_CONTAINER: 'overflow-hidden',
  MODAL_OVERLAY: 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50',
  MODAL_CONTAINER: 'relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800'
}

// Common Icons
export const ICONS = {
  LOADING: 'animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600',
  SUCCESS: 'text-green-600',
  ERROR: 'text-red-600',
  WARNING: 'text-yellow-600',
  INFO: 'text-blue-600',
  EDIT: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  DELETE: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  ADD: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
  CLOSE: 'M6 18L18 6M6 6l12 12',
  CHEVRON_LEFT: 'M15 19l-7-7 7-7',
  CHEVRON_RIGHT: 'M9 5l7 7-7 7',
  CHEVRON_UP: 'M5 15l7-7 7 7',
  CHEVRON_DOWN: 'M19 9l-7 7-7-7'
}

// Common Date Formats
export const DATE_FORMATS = {
  SHORT: 'MMM DD, YYYY',
  LONG: 'MMMM DD, YYYY',
  DATETIME: 'MMM DD, YYYY HH:mm',
  TIME: 'HH:mm',
  ISO: 'YYYY-MM-DDTHH:mm:ss.sssZ'
}

// Common Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_VISIBLE_PAGES: 5
}

// Common Filters
export const FILTERS = {
  SORT_OPTIONS: {
    NAME_ASC: 'name_asc',
    NAME_DESC: 'name_desc',
    CREATED_ASC: 'created_asc',
    CREATED_DESC: 'created_desc',
    UPDATED_ASC: 'updated_asc',
    UPDATED_DESC: 'updated_desc'
  },
  
  DEFAULT_SORT: 'created_desc'
}

// Common Table Actions
export const TABLE_ACTIONS = {
  VIEW: 'view',
  EDIT: 'edit',
  DELETE: 'delete',
  CLONE: 'clone',
  ASSIGN: 'assign',
  EXECUTE: 'execute'
}

// Common Modal Sizes
export const MODAL_SIZES = {
  SMALL: 'sm',
  MEDIUM: 'md',
  LARGE: 'lg',
  EXTRA_LARGE: 'xl',
  FULL: 'full'
}

// Common Animation Durations
export const ANIMATIONS = {
  FAST: '150ms',
  NORMAL: '300ms',
  SLOW: '500ms'
}

// Common Z-Index Values
export const Z_INDEX = {
  DROPDOWN: 1000,
  STICKY: 1020,
  FIXED: 1030,
  MODAL_BACKDROP: 1040,
  MODAL: 1050,
  POPOVER: 1060,
  TOOLTIP: 1070
}

// Common Breakpoints
export const BREAKPOINTS = {
  SM: '640px',
  MD: '768px',
  LG: '1024px',
  XL: '1280px',
  '2XL': '1536px'
}

// Common Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme',
  LANGUAGE: 'language',
  TABLE_PREFERENCES: 'table_preferences',
  FORM_DRAFTS: 'form_drafts'
}

// Common Event Names
export const EVENTS = {
  // Global events
  GLOBAL_ERROR: 'global:error',
  GLOBAL_SUCCESS: 'global:success',
  GLOBAL_LOADING: 'global:loading',
  
  // User events
  USER_LOGIN: 'user:login',
  USER_LOGOUT: 'user:logout',
  USER_UPDATE: 'user:update',
  
  // Data events
  DATA_REFRESH: 'data:refresh',
  DATA_UPDATE: 'data:update',
  DATA_DELETE: 'data:delete',
  
  // UI events
  MODAL_OPEN: 'modal:open',
  MODAL_CLOSE: 'modal:close',
  SIDEBAR_TOGGLE: 'sidebar:toggle'
}

// Common HTTP Status Codes
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503
}

// Common Error Codes
export const ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  DUPLICATE_KEY: 'DUPLICATE_KEY',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  TIMEOUT: 'TIMEOUT'
}
